import { useEffect, useState } from "react";
import { Eye, Users, FileDown, BookOpen } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { api } from "@/lib/api";

const RANGES = [7, 30, 90, 365];
const regionName = new Intl.DisplayNames(["es"], { type: "region" });
const country = (k) => {
  try { return k?.code ? regionName.of(k.code) : k?.country || "Desconocido"; } catch { return k?.country || "Desconocido"; }
};
const flag = (code) => (code ? code.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0))) : "");

const Kpi = ({ icon: Icon, label, value, id }) => (
  <div className="card-fv p-5" data-testid={`admin-stats-kpi-${id}`}>
    <Icon size={20} className="text-[#8B2B22]" />
    <p className="f-display mt-3 text-3xl">{value ?? "—"}</p>
    <p className="text-xs uppercase tracking-[0.18em] text-[#5C635E]">{label}</p>
  </div>
);

const RankList = ({ title, rows, render, id }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="card-fv p-5" data-testid={`admin-stats-${id}`}>
      <p className="f-display text-lg">{title}</p>
      {rows.length === 0 && <p className="mt-3 text-sm text-[#5C635E]">Sin datos todavía.</p>}
      <ul className="mt-4 space-y-3">
        {rows.map((r, i) => (
          <li key={i}>
            <div className="flex justify-between text-sm"><span>{render(r.key)}</span><span className="font-semibold">{r.count}</span></div>
            <div className="mt-1 h-1.5 bg-[#F3EAD7]"><div className="h-full bg-[#8B2B22]" style={{ width: `${(r.count / max) * 100}%` }} /></div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const AdminStats = () => {
  const [days, setDays] = useState(30);
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats", { params: { days } }).then((r) => setS(r.data)); }, [days]);
  const countryRow = (k) => `${flag(k?.code)} ${country(k)}`;

  return (
    <div className="space-y-6" data-testid="admin-stats">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[#5C635E]">Solo visible para la administración. Tus propias visitas con sesión iniciada no se cuentan.</p>
        <div className="flex gap-1">
          {RANGES.map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`admin-stats-range-${d}`}
              className={`px-3 py-1.5 text-sm border ${days === d ? "bg-[#1D3324] text-white border-[#1D3324]" : "bg-white border-[#8B2B22]/15"}`}>
              {d === 365 ? "1 año" : `${d} días`}
            </button>
          ))}
        </div>
      </div>
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        <Kpi icon={Eye} label="Visitas" value={s?.visits} id="visits" />
        <Kpi icon={Users} label="Visitantes únicos" value={s?.visitors} id="visitors" />
        <Kpi icon={FileDown} label="Descargas del catálogo" value={s?.downloads} id="downloads" />
        <Kpi icon={BookOpen} label="Vistas online del catálogo" value={s?.views} id="views" />
      </div>
      <div className="card-fv p-5" data-testid="admin-stats-chart">
        <p className="f-display text-lg mb-4">Visitas por día</p>
        <div className="h-64">
          <ResponsiveContainer>
            <AreaChart data={s?.series || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(5).split("-").reverse().join("/")} fontSize={11} />
              <YAxis allowDecimals={false} fontSize={11} width={30} />
              <Tooltip labelFormatter={(d) => new Date(d + "T12:00").toLocaleDateString("es-CR")} formatter={(v) => [v, "Visitas"]} />
              <Area type="monotone" dataKey="visits" stroke="#8B2B22" fill="#8B2B22" fillOpacity={0.15} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <RankList id="countries" title="Visitas por país" rows={s?.countries || []} render={countryRow} />
        <RankList id="cities" title="Visitas por ciudad" rows={s?.cities || []} render={(k) => `${flag(k?.code)} ${k?.city || "Desconocida"} · ${country(k)}`} />
        <RankList id="download-countries" title="Descargas / vistas por país" rows={s?.download_countries || []} render={countryRow} />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <RankList id="pages" title="Páginas más vistas" rows={s?.pages || []} render={(p) => p || "/"} />
        <div className="card-fv p-5" data-testid="admin-stats-recent">
          <p className="f-display text-lg">Últimas descargas y vistas del catálogo</p>
          {!s?.recent_downloads?.length && <p className="mt-3 text-sm text-[#5C635E]">Sin descargas todavía.</p>}
          <ul className="mt-3 divide-y text-sm">
            {(s?.recent_downloads || []).map((e) => (
              <li key={e.id} className="flex justify-between gap-3 py-2">
                <span>{e.name && <strong className="mr-1">{e.name} ·</strong>}{flag(e.country_code)} {e.city ? `${e.city}, ` : ""}{country({ code: e.country_code, country: e.country })}</span>
                <span className="text-[#5C635E]">{e.type === "catalog_download" ? "Descarga" : "Vista"} · {new Date(e.created_at).toLocaleString("es-CR")}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
